/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * cwl-flatten.ts - Utilities for finding the root workflow and flattening
 * nested CWL workflows into a single workflow structure.
 *
 * Given a CWL $graph with multiple workflows linked via steps (e.g., workflow A
 * has a step that runs workflow B, which may have steps running workflow C),
 * these utilities:
 *   1. Identify the root/top-level workflow (not referenced by any other workflow)
 *   2. Flatten all nested workflows into a single workflow map
 */

import { Workflow as WorkflowModel, WorkflowStep, Process } from 'cwlts/mappings/v1.0';

/** Deep-clone utility for CWL structures */
const clone = <T>(x: T): T => JSON.parse(JSON.stringify(x));

/**
 * Extracts the workflow ID from a step's run reference.
 * Handles both "#workflow-id" and "workflow-id" formats.
 */
const extractRunId = (run: unknown): string | null => {
  if (typeof run === 'string') {
    return run.replace(/^#/, '');
  }
  return null;
};

/**
 * Creates a map of workflow ID to workflow for quick lookup.
 * @param graph - The $graph array
 * @returns Map of workflow ID to WorkflowModel
 */
const buildWorkflowMap = (graph: Process[]): Map<string, WorkflowModel> => {
  const map = new Map<string, WorkflowModel>();
  for (const process of graph) {
    if (process.class?.toLowerCase() === 'workflow') {
      const workflow = process as WorkflowModel;
      if (workflow.id) {
        map.set(workflow.id, workflow);
      }
    }
  }
  return map;
};

/**
 * Recursively flattens a workflow by inlining all sub-workflows.
 *
 * @param workflow - The workflow to flatten
 * @param workflowMap - Map of workflow IDs to workflows
 * @param outputMappings - Accumulated output mappings (stepId/output -> actual source)
 * @param visited - Set of visited workflow IDs (to detect cycles)
 * @returns Object containing new steps
 */
const flattenWorkflowRecursive = (
  workflow: WorkflowModel,
  workflowMap: Map<string, WorkflowModel>,
  outputMappings: Record<string, string | string[]>,
  visited: Set<string>
): Record<string, WorkflowStep> => {
  const workflowId = workflow.id ?? 'unknown';

  // Cycle detection
  if (visited.has(workflowId)) {
    console.warn(`Cycle detected: workflow '${workflowId}' already visited`);
    return {};
  }
  visited.add(workflowId);

  const steps = workflow.steps ?? {};
  const stepEntries: [string, WorkflowStep][] = Array.isArray(steps)
    ? steps.map((s) => [s.id ?? '', s])
    : Object.entries(steps);

  const newSteps: Record<string, WorkflowStep> = {};

  for (const [stepId, step] of stepEntries) {
    const runId = extractRunId(step.run);
    const subWorkflow = runId ? workflowMap.get(runId) : null;

    if (subWorkflow) {
      // This step runs a sub-workflow - recurse and flatten
      // First, recursively flatten the sub-workflow
      const subSteps = flattenWorkflowRecursive(subWorkflow, workflowMap, outputMappings, new Set(visited));

      // Merge sub-workflow steps
      Object.assign(newSteps, subSteps);

      // Build output mappings for this sub-workflow step
      // e.g., node_eodag/result_files -> node_download/result_files
      const subOutputs = subWorkflow.outputs ?? {};
      const subOutputEntries: [string, { outputSource?: string | string[] }][] = Array.isArray(subOutputs)
        ? subOutputs.map((o) => [o.id ?? '', o])
        : Object.entries(subOutputs);

      for (const [outId, outDef] of subOutputEntries) {
        const src = outDef.outputSource;
        if (typeof src === 'string') {
          // Resolve through existing mappings if needed
          outputMappings[`${stepId}/${outId}`] = outputMappings[src] ?? src;
        } else if (Array.isArray(src)) {
          outputMappings[`${stepId}/${outId}`] = src.map((s) => (outputMappings[s] as string) ?? s);
        }
      }

      // Rewire input bindings from sub-workflow step to point to workflow inputs
      // This handles cases where sub-workflow inputs come from parent workflow inputs
      const stepIn = step.in ?? {};
      const inEntries: [string, unknown][] = Array.isArray(stepIn)
        ? stepIn.map((inp) => [inp.id ?? '', inp])
        : Object.entries(stepIn);

      for (const [inpName, inpVal] of inEntries) {
        let sourceValue: string | string[] | undefined;
        if (typeof inpVal === 'string') {
          sourceValue = inpVal;
        } else if (typeof inpVal === 'object' && inpVal !== null) {
          const obj = inpVal as { source?: string | string[] };
          sourceValue = obj.source;
        }
        if (sourceValue) {
          // Map sub-workflow input name to parent source
          // This will be used by steps inside sub-workflow that reference this input
          outputMappings[inpName] =
            typeof sourceValue === 'string'
              ? (outputMappings[sourceValue] ?? sourceValue)
              : sourceValue.map((s) => (outputMappings[s] as string) ?? s);
        }
      }
    } else {
      // This step runs a CommandLineTool or similar - clone and rewrite sources
      const newStep = clone(step);

      // Rewrite input bindings using output mappings
      if (newStep.in) {
        const inEntries: [string, unknown][] = Array.isArray(newStep.in)
          ? newStep.in.map((inp) => [inp.id ?? '', inp])
          : Object.entries(newStep.in);

        const newIn: Record<string, unknown> = {};

        for (const [inpName, inpVal] of inEntries) {
          if (typeof inpVal === 'string') {
            // Resolve through output mappings
            newIn[inpName] = outputMappings[inpVal] ?? inpVal;
          } else if (typeof inpVal === 'object' && inpVal !== null) {
            const obj = clone(inpVal as Record<string, unknown>);
            if ('source' in obj && obj.source) {
              if (typeof obj.source === 'string') {
                obj.source = outputMappings[obj.source] ?? obj.source;
              } else if (Array.isArray(obj.source)) {
                obj.source = (obj.source as string[]).map((s) => (outputMappings[s] as string) ?? s);
              }
            }
            newIn[inpName] = obj;
          } else {
            newIn[inpName] = inpVal;
          }
        }

        newStep.in = newIn as unknown as typeof newStep.in;
      }

      newSteps[stepId] = newStep;
    }
  }

  return newSteps;
};

/**
 * Flattens a workflow graph by inlining all nested sub-workflows.
 * Returns a new workflow with all steps flattened to a single level.
 *
 * @param rootWorkflow - The root/top-level workflow to flatten
 * @param graph - The full $graph array containing all processes
 * @returns A new flattened WorkflowModel
 */
export const flattenWorkflow = (rootWorkflow: WorkflowModel, graph: Process[]): WorkflowModel => {
  const workflowMap = buildWorkflowMap(graph);
  const result = clone(rootWorkflow);

  // Output mappings: maps step/output references to their resolved sources
  // e.g., "node_wf/output_cogs" -> "node_cog/output_cog"
  // e.g., "node_eodag/result_files" -> "node_download/result_files"
  const outputMappings: Record<string, string | string[]> = {};

  // Recursively flatten starting from root workflow
  const newSteps = flattenWorkflowRecursive(rootWorkflow, workflowMap, outputMappings, new Set());

  // Update workflow outputs using output mappings
  if (result.outputs) {
    const outputEntries: [string, { outputSource?: string | string[] }][] = Array.isArray(result.outputs)
      ? result.outputs.map((o) => [o.id ?? '', o])
      : Object.entries(result.outputs);

    for (const [, outDef] of outputEntries) {
      if (typeof outDef.outputSource === 'string') {
        outDef.outputSource = (outputMappings[outDef.outputSource] as string) ?? outDef.outputSource;
      } else if (Array.isArray(outDef.outputSource)) {
        outDef.outputSource = outDef.outputSource.map((s) => (outputMappings[s] as string) ?? s);
      }
    }
  }

  result.steps = newSteps as unknown as typeof result.steps;

  // Ensure cwlVersion is set
  if (!result.cwlVersion) {
    result.cwlVersion = 'v1.0';
  }

  return result;
};
